import React, { useEffect, useCallback } from 'react';
import { getCdnImage } from '../lib/imageUtils';

export default function Lightbox({ images = [], currentIndex = 0, isOpen, onClose, onPrev, onNext }) {
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    },
    [isOpen, onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex] || images[0];
  const currentSrc = typeof currentImage === 'string' ? currentImage : currentImage.src || currentImage.url;
  const currentAlt = typeof currentImage === 'object' ? currentImage.alt || currentImage.title : 'Billede fra entreprenørprojekt';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 backdrop-blur-md p-4 sm:p-6 select-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning i fuld skærm"
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-4 z-20 text-white">
        <div className="text-sm font-medium tracking-wide text-slate-300">
          Billede <span className="text-emerald-400 font-semibold">{currentIndex + 1}</span> af {images.length}
        </div>
        <button
          onClick={onClose}
          className="rounded-full bg-slate-800/80 hover:bg-slate-700 text-white p-2.5 transition-colors cursor-pointer border border-slate-700"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
        </button>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 sm:left-6 z-20 rounded-full bg-slate-900/80 hover:bg-emerald-600 text-white p-3 sm:p-4 transition-colors cursor-pointer border border-slate-700 shadow-xl"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-xl" aria-hidden="true"></i>
        </button>
      )}

      {/* Image container */}
      <div
        className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getCdnImage(currentSrc, { width: 1600, quality: 90 })}
          alt={currentAlt}
          className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl border border-slate-800"
        />
        {currentAlt && (
          <p className="mt-3 text-center text-sm sm:text-base text-slate-300 font-medium px-4 max-w-2xl">
            {currentAlt}
          </p>
        )}
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 sm:right-6 z-20 rounded-full bg-slate-900/80 hover:bg-emerald-600 text-white p-3 sm:p-4 transition-colors cursor-pointer border border-slate-700 shadow-xl"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-xl" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
